import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, CalendarDays, Clock, CheckCircle2, Trash2, AlertTriangle, LifeBuoy, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ACTIVITIES_START, PERIODS, PERIOD_LABELS } from "@/lib/constants";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const STATUS = {
  en_attente: { label: "Dispo — en attente", short: "Attente", dot: "bg-amber-400", pill: "bg-amber-100 text-amber-700", cell: "bg-amber-50 border-amber-300 text-amber-700" },
  libre: { label: "Disponible (validé)", short: "Dispo", dot: "bg-emerald-400", pill: "bg-emerald-100 text-emerald-700", cell: "bg-emerald-50 border-emerald-300 text-emerald-700" },
  reserve: { label: "Réservé", short: "Réservé", dot: "bg-sky-400", pill: "bg-sky-100 text-sky-700", cell: "bg-sky-50 border-sky-300 text-sky-700" },
  absence_attente: { label: "Absence — en attente", short: "Abs?", dot: "bg-orange-400", pill: "bg-orange-100 text-orange-700", cell: "bg-orange-50 border-orange-300 text-orange-700" },
  absence: { label: "Absent (validé)", short: "Absent", dot: "bg-slate-400", pill: "bg-slate-200 text-slate-600", cell: "bg-slate-100 border-slate-300 text-slate-500" },
};

const PENDING = ["en_attente", "absence_attente"];
const toKey = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const mondayOf = (date) => { const x = new Date(date); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); x.setHours(0, 0, 0, 0); return x; };

export default function AnimatorSpace() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [calView, setCalView] = useState("month");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("dispo");
  const [period, setPeriod] = useState("matin");
  const [myRes, setMyRes] = useState([]);
  const [history, setHistory] = useState({ withdrawals: [], replacements: [] });

  useEffect(() => {
    if (!loading && (!user || user.role !== "animateur")) navigate("/");
  }, [user, loading, navigate]);

  const load = useCallback(() => {
    api.get("/animator/availability").then((r) => setSlots(r.data)).catch(() => {});
    api.get("/animator/reservations").then((r) => setMyRes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user?.role === "animateur") load(); }, [user, load]);
  useEffect(() => {
    if (user?.animator_id) api.get(`/admin/animators/${user.animator_id}/history`).then((r) => setHistory(r.data)).catch(() => {});
  }, [user, myRes]);

  const withdraw = async (r) => {
    if (!window.confirm(`Confirmer votre désistement pour « ${r.item_title} » du ${new Date(r.slot_date).toLocaleDateString("fr-BE")} ? L'administration sera alertée pour trouver un remplaçant.`)) return;
    const reason = window.prompt("Motif (facultatif) :") || "";
    try {
      await api.post(`/animator/reservations/${r.id}/withdraw`, { reason });
      toast.success("Désistement enregistré — l'administration va organiser un remplacement");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!user || user.role !== "animateur") return null;

  const byDatePeriod = {};
  slots.forEach((s) => { byDatePeriod[`${s.date}|${s.period || "matin"}`] = s; });
  const todayKey = toKey(now.getFullYear(), now.getMonth(), now.getDate());
  const minKey = ACTIVITIES_START > todayKey ? ACTIVITIES_START : todayKey;

  const propose = async (key, p) => {
    setBusy(true);
    try {
      await api.post("/animator/availability", { date: key, type: mode, period: p });
      toast.success(mode === "absence" ? "Absence proposée — en attente de validation" : "Disponibilité proposée — en attente de validation");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (slot) => {
    if (!slot) return;
    setBusy(true);
    try {
      await api.delete(`/animator/availability/${slot.id}`);
      toast.success("Créneau retiré");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const actCell = (key, p, disabled) => {
    if (busy || disabled) return;
    const slot = byDatePeriod[`${key}|${p}`];
    if (!slot) return propose(key, p);
    if (PENDING.includes(slot.status)) return remove(slot);
    if (slot.status === "reserve") return toast.info("Ce créneau est réservé, contactez l'administration pour le modifier.");
    return toast.info("Cet élément est validé, contactez l'administration pour le modifier.");
  };

  // month grid
  const first = new Date(view.y, view.m, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const changeMonth = (delta) => {
    let m = view.m + delta, y = view.y;
    if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
    setView({ y, m });
  };

  // week grid
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
  const changeWeek = (delta) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + delta * 7); setWeekStart(d); };
  const weekTitle = `Semaine du ${weekStart.toLocaleDateString("fr-BE", { day: "numeric", month: "long", year: "numeric" })}`;

  const upcoming = [...slots].sort((a, b) => a.date.localeCompare(b.date) || (a.period || "").localeCompare(b.period || ""));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="animator-space">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Espace animateur</span>
        <h1 className="font-heading font-black text-4xl text-slate-900 mt-1">Mes disponibilités</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Bonjour {user.name} 👋 Choisissez un créneau (matin ou après-midi) puis cliquez sur un jour. Vos
          propositions sont soumises à la validation de l'administration avant d'être visibles par les enseignants.
          Les activités démarrent le 5 octobre 2026.
        </p>
        <button
          onClick={() => navigate("/espace-animateur/fiches")}
          data-testid="animator-deroules-link"
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white font-bold text-sm hover:bg-slate-700 transition-colors"
        >
          <FileText className="w-4 h-4" /> Fiches de déroulé des séances
        </button>
      </motion.div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="animator-mode">
        <span className="text-sm font-semibold text-slate-500 mr-1">Je marque un jour :</span>
        <button onClick={() => setMode("dispo")} data-testid="mode-dispo" className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${mode === "dispo" ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Disponible</button>
        <button onClick={() => setMode("absence")} data-testid="mode-absence" className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${mode === "absence" ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Absence</button>
      </div>

      {calView === "month" && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="animator-period">
          <span className="text-sm font-semibold text-slate-500 mr-1">Créneau :</span>
          {PERIODS.map((p) => (
            <button key={p.value} onClick={() => setPeriod(p.value)} data-testid={`period-${p.value}`} className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${period === p.value ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{p.label}</button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center justify-end gap-2 mb-4">
            <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="animator-calview">
              <button onClick={() => setCalView("month")} data-testid="calview-month" className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${calView === "month" ? "bg-white shadow text-sky-600" : "text-slate-500"}`}>Mois</button>
              <button onClick={() => setCalView("week")} data-testid="calview-week" className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${calView === "week" ? "bg-white shadow text-sky-600" : "text-slate-500"}`}>Semaine</button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <button onClick={() => calView === "month" ? changeMonth(-1) : changeWeek(-1)} data-testid="cal-prev" className="grid place-items-center w-10 h-10 rounded-full bg-slate-100 hover:bg-sky-100 transition-colors">
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <h2 className="font-heading font-extrabold text-lg sm:text-xl text-slate-900 text-center" data-testid="cal-title">
              {calView === "month" ? `${MONTHS[view.m]} ${view.y}` : weekTitle}
            </h2>
            <button onClick={() => calView === "month" ? changeMonth(1) : changeWeek(1)} data-testid="cal-next" className="grid place-items-center w-10 h-10 rounded-full bg-slate-100 hover:bg-sky-100 transition-colors">
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
          </div>

          {calView === "month" ? (
            <>
              <div className="grid grid-cols-7 gap-1.5 mb-2">
                {WEEKDAYS.map((w) => <div key={w} className="text-center text-xs font-bold text-slate-400 py-1">{w}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {cells.map((d, i) => {
                  if (d === null) return <div key={`e-${i}`} />;
                  const key = toKey(view.y, view.m, d);
                  const disabled = key < minKey;
                  const selSlot = byDatePeriod[`${key}|${period}`];
                  const selSt = selSlot ? STATUS[selSlot.status] : null;
                  return (
                    <button
                      key={key}
                      disabled={disabled}
                      onClick={() => actCell(key, period, disabled)}
                      data-testid={`cal-day-${key}`}
                      className={`relative aspect-square rounded-2xl border text-sm font-semibold transition-all
                        ${disabled ? "border-transparent text-slate-300 cursor-not-allowed"
                          : selSt ? `${selSt.cell} hover:scale-[1.04]`
                          : "border-slate-200 text-slate-700 hover:border-sky-400 hover:bg-sky-50 hover:scale-[1.04]"}`}
                    >
                      <span className="absolute top-1.5 left-2">{d}</span>
                      {!disabled && (
                        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                          {PERIODS.map((p) => {
                            const s = byDatePeriod[`${key}|${p.value}`];
                            return <span key={p.value} title={p.short} className={`w-1.5 h-1.5 rounded-full ${s ? STATUS[s.status].dot : "bg-slate-200"}`} />;
                          })}
                        </span>
                      )}
                      {key === todayKey && <span className="absolute top-1.5 right-2 w-1.5 h-1.5 rounded-full bg-slate-900" />}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="overflow-x-auto" data-testid="animator-week-grid">
              <div className="min-w-[560px]">
                <div className="grid grid-cols-8 gap-1.5 mb-1.5">
                  <div />
                  {weekDays.map((d) => {
                    const key = toKey(d.getFullYear(), d.getMonth(), d.getDate());
                    return (
                      <div key={key} className={`text-center py-1 ${key === todayKey ? "text-sky-600" : "text-slate-500"}`}>
                        <div className="text-[11px] font-bold uppercase">{WEEKDAYS[(d.getDay() + 6) % 7]}</div>
                        <div className="text-sm font-extrabold">{d.getDate()}</div>
                      </div>
                    );
                  })}
                </div>
                {PERIODS.map((p) => (
                  <div key={p.value} className="grid grid-cols-8 gap-1.5 mb-1.5 items-stretch">
                    <div className="flex items-center text-xs font-bold text-slate-500 pr-1">{p.short}</div>
                    {weekDays.map((d) => {
                      const key = toKey(d.getFullYear(), d.getMonth(), d.getDate());
                      const disabled = key < minKey;
                      const slot = byDatePeriod[`${key}|${p.value}`];
                      const st = slot ? STATUS[slot.status] : null;
                      return (
                        <button
                          key={`${key}-${p.value}`}
                          disabled={disabled}
                          onClick={() => actCell(key, p.value, disabled)}
                          data-testid={`week-cell-${key}-${p.value}`}
                          className={`h-14 rounded-xl border text-[11px] font-bold transition-all
                            ${disabled ? "border-transparent bg-slate-50 text-slate-300 cursor-not-allowed"
                              : st ? `${st.cell} hover:scale-[1.03]`
                              : "border-dashed border-slate-300 text-slate-400 hover:border-sky-400 hover:bg-sky-50"}`}
                        >
                          {st ? st.short : "+"}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-4 mt-6 pt-5 border-t border-slate-100 text-xs font-semibold text-slate-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400" /> Dispo en attente</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-400" /> Disponible (visible profs)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-sky-400" /> Réservé</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-orange-400" /> Absence en attente</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-slate-400" /> Absent</span>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
          <h3 className="font-heading font-extrabold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-sky-500" /> Mes créneaux
          </h3>
          {upcoming.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="animator-no-slots">Aucune disponibilité proposée pour le moment.</p>
          ) : (
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1" data-testid="animator-slots-list">
              {upcoming.map((s) => {
                const st = STATUS[s.status];
                const past = s.date < todayKey;
                return (
                  <div key={s.id} className="flex items-center gap-3 border border-slate-100 rounded-2xl p-3">
                    <span className={`grid place-items-center w-9 h-9 rounded-xl ${st.pill}`}>
                      {s.status === "libre" ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm capitalize ${past ? "text-slate-400" : "text-slate-800"}`}>
                        {new Date(s.date).toLocaleDateString("fr-BE", { weekday: "short", day: "numeric", month: "short" })}
                      </p>
                      <p className="text-[11px] text-slate-400">{PERIOD_LABELS[s.period || "matin"]}</p>
                      <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full mt-0.5 ${st.pill}`}>{st.label}</span>
                    </div>
                    {PENDING.includes(s.status) && (
                      <button onClick={() => remove(s)} data-testid={`animator-del-slot-${s.id}`} className="grid place-items-center w-8 h-8 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
          <h3 className="font-heading font-extrabold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-emerald-500" /> Mes ateliers à animer
          </h3>
          {myRes.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="animator-no-reservations">Aucun atelier attribué à venir.</p>
          ) : (
            <div className="space-y-2" data-testid="animator-reservations-list">
              {myRes.map((r) => (
                <div key={r.id} className="flex items-center gap-3 border border-slate-100 rounded-2xl p-3" data-testid={`animator-res-${r.id}`}>
                  <span className="grid place-items-center w-9 h-9 rounded-xl bg-emerald-100 text-emerald-600"><CheckCircle2 className="w-4 h-4" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">{r.item_title}</p>
                    <p className="text-[11px] text-slate-400 capitalize">{r.slot_date ? new Date(r.slot_date).toLocaleDateString("fr-BE", { weekday: "short", day: "numeric", month: "short" }) : "date à définir"} · {r.school || "-"}</p>
                  </div>
                  <button onClick={() => withdraw(r)} data-testid={`animator-withdraw-${r.id}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Je me désiste
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
          <h3 className="font-heading font-extrabold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-sky-500" /> Historique désistements / remplacements
          </h3>
          {history.withdrawals.length === 0 && history.replacements.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="animator-no-history">Aucun historique pour le moment.</p>
          ) : (
            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="animator-history-list">
              {history.withdrawals.map((rep) => (
                <div key={`w-${rep.id}`} className="flex items-center gap-2 text-sm border border-slate-100 rounded-xl p-2.5">
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-600">Désistement</span>
                  <span className="text-slate-600 truncate">{rep.reservation_title} · {rep.slot_date}</span>
                  <span className="ml-auto text-[11px] text-slate-400">{rep.status === "filled" ? `remplacé par ${rep.filled_by_animator_name}` : "en attente"}</span>
                </div>
              ))}
              {history.replacements.map((rep) => (
                <div key={`r-${rep.id}`} className="flex items-center gap-2 text-sm border border-slate-100 rounded-xl p-2.5">
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Remplacement</span>
                  <span className="text-slate-600 truncate">{rep.reservation_title} · {rep.slot_date}</span>
                  <span className="ml-auto text-[11px] text-slate-400">à la place de {rep.original_animator_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
